import { API_AGENT_DEFAULTERS_EXPORT } from "../../../../utils/apis";
import { NoWrap, numberFormat, returnObject } from "../../../../utils/helpers";

type TAgentDefaultersReportProps = {
  dateFilterKey?: string;
  status?: string;
  noWrapColumns?: string[];
  numberColumns?: string[];
};

const useAgentDefaultersReport = ({
  dateFilterKey,
  noWrapColumns = [],
}: TAgentDefaultersReportProps) => {
  const tableColumns = () => {
    const filterColumns = [
      {
        id: "full_name",
        header: <NoWrap>Agent Name</NoWrap>,
        filter: { type: "text" },
      },
      {
        id: "agent_id",
        header: <NoWrap>Agent ID</NoWrap>,
        filter: { type: "text" },
      },
      {
        id: "amount",
        header: <NoWrap>Outstanding Amount</NoWrap>,
        filter: { type: "number" },
      },
      {
        id: "is_settled",
        header: <NoWrap>Settlement Status</NoWrap>,
        filter: { type: "select", options: ["All", "Settled", "Unsettled"] },
      },
      {
        id: "created_date",
        header: <NoWrap>Created Date</NoWrap>,
        filter: { type: "date" },
      },
      {
        id: "last_updated_date",
        header: <NoWrap>Last Updated</NoWrap>,
        filter: { type: "date" },
      },
    ];

    return [...filterColumns];
  };

  const tableData = (data: any) => {
    return data?.map((item: any) => {
      const amount = parseFloat(item.amount) || 0;
      const daysSinceCreated = Math.floor(
        (new Date().getTime() - new Date(item.created_date).getTime()) /
          (1000 * 60 * 60 * 24),
      );
      const daysSinceUpdated = Math.floor(
        (new Date().getTime() - new Date(item.last_updated_date).getTime()) /
          (1000 * 60 * 60 * 24),
      );

      return {
        ...item,
        [dateFilterKey || "start_date"]: (
          <NoWrap>{item[dateFilterKey || "start_date"]}</NoWrap>
        ),
        full_name: (
          <span
            className={`font-medium ${
              item.full_name === "Unknown"
                ? "text-red-600 bg-red-50 px-2 py-1 rounded"
                : "text-gray-900"
            }`}
          >
            <NoWrap>{item.full_name || "Unknown Agent"}</NoWrap>
          </span>
        ),
        agent_id: <NoWrap>{item.agent_id || "N/A"}</NoWrap>,
        amount: (
          <span
            className={`font-bold ${
              amount === 0
                ? "text-gray-500"
                : amount > 0
                  ? "text-green-600"
                  : "text-red-600"
            }`}
          >
            {numberFormat(Math.abs(amount))}
            {amount !== 0 && (
              <span className="text-xs ml-1">
                {amount > 0 ? "(Credit)" : "(Debt)"}
              </span>
            )}
          </span>
        ),
        absolute_amount: (
          <span className="font-medium text-gray-900">
            {numberFormat(Math.abs(amount))}
          </span>
        ),
        debt_status:
          amount < 0 ? (
            <span
              className={`px-2 py-1 rounded text-xs font-medium ${
                Math.abs(amount) > 500000
                  ? "bg-red-100 text-red-800"
                  : Math.abs(amount) > 100000
                    ? "bg-orange-100 text-orange-800"
                    : Math.abs(amount) > 50000
                      ? "bg-yellow-100 text-yellow-800"
                      : "bg-blue-100 text-blue-800"
              }`}
            >
              {Math.abs(amount) > 500000
                ? "Critical"
                : Math.abs(amount) > 100000
                  ? "High Risk"
                  : Math.abs(amount) > 50000
                    ? "Medium Risk"
                    : "Low Risk"}
            </span>
          ) : amount > 0 ? (
            <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-medium">
              Credit Balance
            </span>
          ) : (
            <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-xs font-medium">
              Balanced
            </span>
          ),
        is_settled: (
          <span
            className={`px-2 py-1 rounded text-xs font-medium ${
              item.is_settled
                ? "bg-green-100 text-green-800"
                : "bg-red-100 text-red-800"
            }`}
          >
            {item.is_settled ? "Settled" : "Unsettled"}
          </span>
        ),
        settlement_priority: (
          <span
            className={`px-2 py-1 rounded text-xs font-medium ${
              !item.is_settled && Math.abs(amount) > 300000
                ? "bg-red-100 text-red-800"
                : !item.is_settled && Math.abs(amount) > 100000
                  ? "bg-orange-100 text-orange-800"
                  : !item.is_settled && Math.abs(amount) > 0
                    ? "bg-yellow-100 text-yellow-800"
                    : "bg-green-100 text-green-800"
            }`}
          >
            {!item.is_settled && Math.abs(amount) > 300000
              ? "Urgent"
              : !item.is_settled && Math.abs(amount) > 100000
                ? "High"
                : !item.is_settled && Math.abs(amount) > 0
                  ? "Normal"
                  : "None"}
          </span>
        ),
        days_since_created:
          daysSinceCreated >= 0 ? (
            <span
              className={`px-2 py-1 rounded text-xs font-medium ${
                daysSinceCreated > 30
                  ? "bg-red-100 text-red-800"
                  : daysSinceCreated > 14
                    ? "bg-orange-100 text-orange-800"
                    : daysSinceCreated > 7
                      ? "bg-yellow-100 text-yellow-800"
                      : "bg-green-100 text-green-800"
              }`}
            >
              {daysSinceCreated} days
            </span>
          ) : (
            <span className="text-gray-400">-</span>
          ),
        days_since_updated:
          daysSinceUpdated >= 0 ? (
            <span
              className={`px-2 py-1 rounded text-xs font-medium ${
                daysSinceUpdated > 7
                  ? "bg-red-100 text-red-800"
                  : daysSinceUpdated > 3
                    ? "bg-orange-100 text-orange-800"
                    : daysSinceUpdated > 1
                      ? "bg-yellow-100 text-yellow-800"
                      : "bg-green-100 text-green-800"
              }`}
            >
              {daysSinceUpdated} days ago
            </span>
          ) : (
            <span className="text-gray-400">-</span>
          ),
        created_date: (
          <NoWrap>{new Date(item.created_date).toLocaleDateString()}</NoWrap>
        ),
        last_updated_date: (
          <NoWrap>
            {new Date(item.last_updated_date).toLocaleDateString()}
          </NoWrap>
        ),
        ...returnObject(
          noWrapColumns.length > 0,
          noWrapColumns.reduce((obj, column) => {
            if (item[column] !== undefined) {
              obj[column] = <NoWrap>{item[column]}</NoWrap>;
            }
            return obj;
          }, {}),
        ),
      };
    });
  };

  const initialFilters = {
    full_name: "",
    agent_id: "",
    amount: "",
    is_settled: "",
    start_date: "",
    end_date: "",
  };

  const getDefaultersStats = (data: any) => {
    const defaulters = data?.results || [];

    const totalDefaulters = defaulters.length;
    const settledCases = defaulters.filter(
      (item: any) => item.is_settled,
    ).length;
    const unsettledCases = totalDefaulters - settledCases;

    const totalOutstanding = defaulters.reduce(
      (sum: number, item: any) => sum + Math.abs(parseFloat(item.amount) || 0),
      0,
    );

    const totalDebt = defaulters
      .filter((item: any) => parseFloat(item.amount) < 0)
      .reduce(
        (sum: number, item: any) => sum + Math.abs(parseFloat(item.amount)),
        0,
      );

    const totalCredit = defaulters
      .filter((item: any) => parseFloat(item.amount) > 0)
      .reduce((sum: number, item: any) => sum + parseFloat(item.amount), 0);

    const averageOutstanding =
      totalDefaulters > 0
        ? (totalOutstanding / totalDefaulters).toFixed(0)
        : "0";

    const settlementRate =
      totalDefaulters > 0
        ? ((settledCases / totalDefaulters) * 100).toFixed(1)
        : "0.0";

    const criticalCases = defaulters.filter(
      (item: any) =>
        !item.is_settled && Math.abs(parseFloat(item.amount)) > 300000,
    ).length;

    const unknownAgents = defaulters.filter(
      (item: any) => item.full_name === "Unknown",
    ).length;

    return {
      totalDefaulters,
      settledCases,
      unsettledCases,
      totalOutstanding,
      totalDebt,
      totalCredit,
      netPosition: totalCredit - totalDebt,
      averageOutstanding,
      settlementRate,
      criticalCases,
      unknownAgents,
    };
  };

  const getRiskBuckets = (data: any) => {
    const defaulters = data?.results || [];
    const buckets = {
      critical: {
        count: 0,
        amount: 0,
        label: "Critical (>500K)",
        threshold: 500000,
      },
      high: {
        count: 0,
        amount: 0,
        label: "High Risk (100K-500K)",
        threshold: 100000,
      },
      medium: {
        count: 0,
        amount: 0,
        label: "Medium Risk (50K-100K)",
        threshold: 50000,
      },
      low: { count: 0, amount: 0, label: "Low Risk (<50K)", threshold: 0 },
    };

    defaulters.forEach((item: any) => {
      const amount = Math.abs(parseFloat(item.amount) || 0);

      if (amount > 500000) {
        buckets.critical.count++;
        buckets.critical.amount += amount;
      } else if (amount > 100000) {
        buckets.high.count++;
        buckets.high.amount += amount;
      } else if (amount > 50000) {
        buckets.medium.count++;
        buckets.medium.amount += amount;
      } else if (amount > 0) {
        buckets.low.count++;
        buckets.low.amount += amount;
      }
    });

    const totalCount = defaulters.length;
    return Object.entries(buckets).map(([key, bucket]) => ({
      category: key,
      label: bucket.label,
      count: bucket.count,
      amount: bucket.amount,
      percentage:
        totalCount > 0 ? ((bucket.count / totalCount) * 100).toFixed(1) : "0.0",
    }));
  };

  const getAgentDefaultersSummary = (data: any) => {
    const defaulters = data?.results || [];

    return defaulters
      .map((item: any) => {
        const amount = parseFloat(item.amount) || 0;
        const daysSinceCreated = Math.floor(
          (new Date().getTime() - new Date(item.created_date).getTime()) /
            (1000 * 60 * 60 * 24),
        );
        const daysSinceUpdated = Math.floor(
          (new Date().getTime() - new Date(item.last_updated_date).getTime()) /
            (1000 * 60 * 60 * 24),
        );

        return {
          ...item,
          absolute_amount: Math.abs(amount),
          debt_amount: amount < 0 ? Math.abs(amount) : 0,
          credit_amount: amount > 0 ? amount : 0,
          days_since_created: daysSinceCreated,
          days_since_updated: daysSinceUpdated,
          risk_level:
            Math.abs(amount) > 500000
              ? "Critical"
              : Math.abs(amount) > 100000
                ? "High"
                : Math.abs(amount) > 50000
                  ? "Medium"
                  : "Low",
          urgency_score:
            (!item.is_settled ? 40 : 0) +
            Math.abs(amount) / 10000 +
            daysSinceCreated / 2 +
            daysSinceUpdated * 2,
          status_category:
            amount === 0 ? "Balanced" : amount > 0 ? "Credit" : "Debt",
        };
      })
      .sort((a: any, b: any) => b.urgency_score - a.urgency_score);
  };

  const getCriticalDefaulters = (data: any, limit: number = 10) => {
    const summary = getAgentDefaultersSummary(data);

    return summary
      .filter((item: any) => !item.is_settled && item.debt_amount > 0)
      .slice(0, limit)
      .map((item: any, index: number) => ({
        rank: index + 1,
        ...item,
        recovery_priority:
          item.debt_amount > 500000 && item.days_since_created > 30
            ? "Immediate"
            : item.debt_amount > 200000 && item.days_since_created > 14
              ? "High"
              : item.debt_amount > 100000
                ? "Medium"
                : "Normal",
      }));
  };

  const getSettlementCandidates = (data: any, limit: number = 10) => {
    const summary = getAgentDefaultersSummary(data);

    return summary
      .filter(
        (item: any) =>
          item.is_settled ||
          (item.debt_amount > 0 && item.days_since_updated <= 7),
      )
      .sort((a: any, b: any) => {
        // Prioritize recently updated items
        if (a.is_settled !== b.is_settled) {
          return a.is_settled ? -1 : 1;
        }
        return a.days_since_updated - b.days_since_updated;
      })
      .slice(0, limit)
      .map((item: any, index: number) => ({
        rank: index + 1,
        ...item,
        settlement_likelihood: item.is_settled
          ? "Completed"
          : item.days_since_updated <= 1
            ? "Very High"
            : item.days_since_updated <= 3
              ? "High"
              : item.days_since_updated <= 7
                ? "Medium"
                : "Low",
      }));
  };

  const getAgingAnalysis = (data: any) => {
    const summary = getAgentDefaultersSummary(data);

    const agingBuckets = {
      fresh: { count: 0, amount: 0, label: "0-7 days" },
      recent: { count: 0, amount: 0, label: "8-30 days" },
      aged: { count: 0, amount: 0, label: "31-90 days" },
      stale: { count: 0, amount: 0, label: "Over 90 days" },
    };

    summary.forEach((item: any) => {
      const days = item.days_since_created;
      const amount = item.absolute_amount;

      if (days <= 7) {
        agingBuckets.fresh.count++;
        agingBuckets.fresh.amount += amount;
      } else if (days <= 30) {
        agingBuckets.recent.count++;
        agingBuckets.recent.amount += amount;
      } else if (days <= 90) {
        agingBuckets.aged.count++;
        agingBuckets.aged.amount += amount;
      } else {
        agingBuckets.stale.count++;
        agingBuckets.stale.amount += amount;
      }
    });

    const totalCount = summary.length;
    return Object.entries(agingBuckets).map(([key, bucket]) => ({
      category: key,
      label: bucket.label,
      count: bucket.count,
      amount: bucket.amount,
      percentage:
        totalCount > 0 ? ((bucket.count / totalCount) * 100).toFixed(1) : "0.0",
    }));
  };

  const exportData = {
    api: API_AGENT_DEFAULTERS_EXPORT,
    fileName: `agent_defaulter_report${new Date().toISOString().split("T")[0]}.xlsx`,
    showCustomDateRange: true,
    start_date: "start_date",
    end_date: "end_date",
  };

  const tableTitle = "Agent Defaulters Performance Report";

  return {
    tableColumns,
    tableData,
    tableTitle,
    exportData,
    initialFilters,
    getDefaultersStats,
    getRiskBuckets,
    getAgentDefaultersSummary,
    getCriticalDefaulters,
    getSettlementCandidates,
    getAgingAnalysis,
  };
};

export default useAgentDefaultersReport;
